// =============================================================================
// SERVICE MODIFICATION INTERFACES AND MODELS
// =============================================================================

export enum ServiceType {
  MXDR = 'mxdr',
  MEDR = 'medr',
  VSS = 'vss',
  MES = 'mes',
  PROFESSIONAL_SERVICES = 'professional_services',
}

export enum ModificationAction {
  ADD = 'add',
  MODIFY = 'modify',
  CANCEL = 'cancel',
}

export enum MXDRServiceType {
  CONNECT = 'connect',
  COMPLETE = 'complete',
  OFFICE_365 = 'office_365',
}

export enum SLALevel {
  ADVANCED = 'advanced',
  PREMIER = 'premier',
}

export enum PenetrationTestType {
  EXTERNAL = 'external',
  INTERNAL = 'internal',
  WEB_APPLICATION = 'web_application',
  INTERNAL_EXTERNAL = 'internal_external',
}

export enum BreachAttackPackage {
  ESSENTIAL = 'essential',
  ADVANCED = 'advanced',
  PREMIUM = 'premium',
}

// =============================================================================
// BASE SERVICE INTERFACES
// =============================================================================

export interface BaseServiceModification {
  id: string;
  serviceType: ServiceType;
  action: ModificationAction;
  reason: string;
  effectiveDate: Date;
  notes?: string;
}

export interface BaseServiceInfo {
  id: string;
  serviceName: string;
  status: 'active' | 'inactive' | 'pending';
  startDate: Date;
  endDate?: Date;
  description: string;
}

// =============================================================================
// MXDR SERVICE INTERFACES
// =============================================================================

export interface MXDRServiceInfo extends BaseServiceInfo {
  serviceType: ServiceType.MXDR;
  serviceSku: string;
  mxdrType: MXDRServiceType;
  extendedLogRetention: boolean;
  govCloud: boolean;
  quantityAdUsers: number;
  quantityServers: number;
  quantityAppliances: number;
  virtualAppliance: boolean;
  applianceModels: string[];
  shippingContact: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  additionalLocations?: string[];
}

export interface MXDRServiceModification extends BaseServiceModification {
  serviceType: ServiceType.MXDR;
  serviceSku?: string;
  mxdrType?: MXDRServiceType;
  extendedLogRetention?: boolean;
  govCloud?: boolean;
  quantityAdUsers?: number;
  quantityServers?: number;
  quantityAppliances?: number;
  virtualAppliance?: boolean;
  applianceModels?: string[];
  shippingContact?: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  additionalLocations?: string[];
}

// =============================================================================
// MEDR SERVICE INTERFACES
// =============================================================================

export interface MEDRServiceInfo extends BaseServiceInfo {
  serviceType: ServiceType.MEDR;
  serviceSku: string;
  medrNetworkDiscovery?: boolean;
  totalMachines: number;
}

export interface MEDRServiceModification extends BaseServiceModification {
  serviceType: ServiceType.MEDR;
  serviceSku?: string;
  medrNetworkDiscovery?: boolean;
  totalMachines?: number;
}

// =============================================================================
// VSS SERVICE INTERFACES
// =============================================================================

export interface VSSServiceInfo extends BaseServiceInfo {
  serviceType: ServiceType.VSS;
  serviceSku: string;
  totalMachines: number;
}

export interface VSSServiceModification extends BaseServiceModification {
  serviceType: ServiceType.VSS;
  serviceSku?: string;
  totalMachines?: number;
}

// =============================================================================
// MES SERVICE INTERFACES
// =============================================================================

export interface MESServiceInfo extends BaseServiceInfo {
  serviceType: ServiceType.MES;
  mailboxes: number;
  domainName: string;
}

export interface MESServiceModification extends BaseServiceModification {
  serviceType: ServiceType.MES;
  mailboxes?: number;
  domainName?: string;
}

// =============================================================================
// PROFESSIONAL SERVICES INTERFACES
// =============================================================================

export interface ProfessionalServicesInfo extends BaseServiceInfo {
  serviceType: ServiceType.PROFESSIONAL_SERVICES;
  penetrationTesting: boolean;
  incidentResponse: boolean;
  vciso: boolean;
  attackSurfaceAssessment: boolean;
  breachAttackAssessment: boolean;
  penetrationTestType?: PenetrationTestType;
  penetrationTestSubnets?: number;
  penetrationTestDomains?: string[];
  penetrationTestStartDate?: Date;
  attackSurfaceSubnets?: number;
  attackSurfaceStartDate?: Date;
  breachAttackPackage?: BreachAttackPackage;
  breachAttackStartDate?: Date;
}

export interface ProfessionalServicesModification extends BaseServiceModification {
  serviceType: ServiceType.PROFESSIONAL_SERVICES;
  penetrationTesting?: boolean;
  incidentResponse?: boolean;
  vciso?: boolean;
  attackSurfaceAssessment?: boolean;
  breachAttackAssessment?: boolean;
  penetrationTestType?: PenetrationTestType;
  penetrationTestSubnets?: number;
  penetrationTestDomains?: string[];
  penetrationTestStartDate?: Date;
  attackSurfaceSubnets?: number;
  attackSurfaceStartDate?: Date;
  breachAttackPackage?: BreachAttackPackage;
  breachAttackStartDate?: Date;
}

// =============================================================================
// UNION TYPES
// =============================================================================

export type ServiceInfo =
  | MXDRServiceInfo
  | MEDRServiceInfo
  | VSSServiceInfo
  | MESServiceInfo
  | ProfessionalServicesInfo;

export type ServiceModification =
  | MXDRServiceModification
  | MEDRServiceModification
  | VSSServiceModification
  | MESServiceModification
  | ProfessionalServicesModification;

// =============================================================================
// FORM CONFIGURATION INTERFACES
// =============================================================================

export interface FormFieldConfig {
  fieldName: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'date' | 'textarea' | 'multiselect';
  required: boolean;
  options?: { value: any; label: string }[];
  dependencies?: {
    field: string;
    value: any;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
  }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    custom?: (value: any) => string | null;
  };
}

export interface ServiceFormConfig {
  serviceType: ServiceType;
  title: string;
  description: string;
  fields: FormFieldConfig[];
}

// =============================================================================
// SERVICE CONFIGURATIONS
// =============================================================================

export const SERVICE_CONFIGURATIONS: Record<ServiceType, ServiceFormConfig> = {
  [ServiceType.MXDR]: {
    serviceType: ServiceType.MXDR,
    title: 'mXDR Service',
    description: 'Extended Detection and Response service configuration',
    fields: [
      {
        fieldName: 'serviceSku',
        label: 'Service SKU',
        type: 'text',
        required: true,
      },
      {
        fieldName: 'mxdrType',
        label: 'mXDR Service Type',
        type: 'select',
        required: true,
        options: [
          { value: MXDRServiceType.CONNECT, label: 'mXDR Connect' },
          { value: MXDRServiceType.COMPLETE, label: 'mXDR Complete' },
          { value: MXDRServiceType.OFFICE_365, label: 'mXDR 365' },
        ],
      },
      {
        fieldName: 'extendedLogRetention',
        label: 'Extended Log Retention',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'govCloud',
        label: 'Gov Cloud',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'quantityAdUsers',
        label: 'Number of AD Users',
        type: 'number',
        required: true,
        validation: { min: 1 },
      },
      {
        fieldName: 'quantityServers',
        label: 'Number of Servers',
        type: 'number',
        required: true,
        validation: { min: 0 },
      },
      {
        fieldName: 'quantityAppliances',
        label: 'Number of Appliances',
        type: 'number',
        required: false,
        validation: { min: 0 },
      },
      {
        fieldName: 'virtualAppliance',
        label: 'Virtual Appliance',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'applianceModels',
        label: 'Appliance Models',
        type: 'multiselect',
        required: false,
        dependencies: [{ field: 'quantityAppliances', value: 0, operator: 'greater_than' }],
      },
    ],
  },
  [ServiceType.MEDR]: {
    serviceType: ServiceType.MEDR,
    title: 'mEDR Service',
    description: 'Managed Endpoint Detection and Response service configuration',
    fields: [
      {
        fieldName: 'serviceSku',
        label: 'Service SKU',
        type: 'text',
        required: true,
      },
      {
        fieldName: 'medrNetworkDiscovery',
        label: 'mEDR Network Discovery (Ranger)',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'totalMachines',
        label: 'Total Number of Machines',
        type: 'number',
        required: true,
        validation: { min: 1 },
      },
    ],
  },
  [ServiceType.VSS]: {
    serviceType: ServiceType.VSS,
    title: 'VSS Service',
    description: 'Vulnerability Scanning Service configuration',
    fields: [
      {
        fieldName: 'serviceSku',
        label: 'Service SKU',
        type: 'text',
        required: true,
      },
      {
        fieldName: 'vssEssential',
        label: 'VSS Essential',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'vssEnhanced',
        label: 'VSS Enhanced',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'totalMachines',
        label: 'Total Number of Machines',
        type: 'number',
        required: true,
        validation: { min: 1 },
      },
    ],
  },
  [ServiceType.MES]: {
    serviceType: ServiceType.MES,
    title: 'mES Service',
    description: 'Managed Email Security service configuration',
    fields: [
      {
        fieldName: 'serviceSku',
        label: 'Service SKU',
        type: 'text',
        required: true,
      },
      {
        fieldName: 'mailboxes',
        label: 'Number of Mailboxes',
        type: 'number',
        required: true,
        validation: { min: 1 },
      },
      {
        fieldName: 'domainName',
        label: 'Domain Name',
        type: 'text',
        required: true,
        validation: { pattern: '^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\\.[a-zA-Z]{2,}$' },
      },
    ],
  },
  [ServiceType.PROFESSIONAL_SERVICES]: {
    serviceType: ServiceType.PROFESSIONAL_SERVICES,
    title: 'Professional Services',
    description: 'Professional services configuration',
    fields: [
      {
        fieldName: 'serviceSku',
        label: 'Service SKU',
        type: 'text',
        required: true,
      },
      {
        fieldName: 'penetrationTesting',
        label: 'Penetration Testing',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'incidentResponse',
        label: 'Incident Response (IRaaS)',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'vciso',
        label: 'vCISO',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'attackSurfaceAssessment',
        label: 'Attack Surface Assessment',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'breachAttackAssessment',
        label: 'Breach Attack Assessment',
        type: 'boolean',
        required: false,
      },
      {
        fieldName: 'penetrationTestType',
        label: 'Penetration Test Type',
        type: 'select',
        required: false,
        dependencies: [{ field: 'penetrationTesting', value: true, operator: 'equals' }],
        options: [
          { value: PenetrationTestType.EXTERNAL, label: 'External' },
          { value: PenetrationTestType.INTERNAL, label: 'Internal' },
          { value: PenetrationTestType.WEB_APPLICATION, label: 'Web Application' },
          { value: PenetrationTestType.INTERNAL_EXTERNAL, label: 'Internal & External' },
        ],
      },
      {
        fieldName: 'penetrationTestSubnets',
        label: 'Number of Subnets to Test',
        type: 'number',
        required: false,
        dependencies: [{ field: 'penetrationTesting', value: true, operator: 'equals' }],
        validation: { min: 1 },
      },
      {
        fieldName: 'penetrationTestStartDate',
        label: 'Penetration Test Start Date',
        type: 'date',
        required: false,
        dependencies: [{ field: 'penetrationTesting', value: true, operator: 'equals' }],
      },
      {
        fieldName: 'attackSurfaceSubnets',
        label: 'Number of IP Subnets',
        type: 'number',
        required: false,
        dependencies: [{ field: 'attackSurfaceAssessment', value: true, operator: 'equals' }],
        validation: { min: 1 },
      },
      {
        fieldName: 'attackSurfaceStartDate',
        label: 'Attack Surface Assessment Start Date',
        type: 'date',
        required: false,
        dependencies: [{ field: 'attackSurfaceAssessment', value: true, operator: 'equals' }],
      },
      {
        fieldName: 'breachAttackPackage',
        label: 'Breach Attack Assessment Package',
        type: 'select',
        required: false,
        dependencies: [{ field: 'breachAttackAssessment', value: true, operator: 'equals' }],
        options: [
          { value: BreachAttackPackage.ESSENTIAL, label: 'Essential' },
          { value: BreachAttackPackage.ADVANCED, label: 'Advanced' },
          { value: BreachAttackPackage.PREMIUM, label: 'Premium' },
        ],
      },
      {
        fieldName: 'breachAttackStartDate',
        label: 'Breach Attack Assessment Start Date',
        type: 'date',
        required: false,
        dependencies: [{ field: 'breachAttackAssessment', value: true, operator: 'equals' }],
      },
    ],
  },
};
